<template>
  <div
    class="measure-graph"
    :class="measureGraphClass"
    ref="graphDiv"
  >
    <img
      v-if="isDebug"
      class="measure-graph__bg"
      :src="graphSrc"/>

    <table class="measure-graph__table">
      <thead>
      <tr>
        <td colspan="5" class="text-center px-2">
          <strong>
            <span
              v-if="title"
              class="print-only-inline"
            ><span v-html="title"></span> &mdash;&nbsp;</span>
            {{ graphTitle }}
          </strong>
        </td>
      </tr>
      <tr>
        <th>Дата</th>
        <th>Возраст</th>
        <th>Рост</th>
        <th>Вес</th>
        <th>ИМТ</th>
      </tr>
      </thead>
      <tbody>
      <tr
        v-for="(item, key) in lastItems"
        :key="key"
      >
        <td>{{ item.dt || '&nbsp;' }}</td>
        <td>{{ item.age || '&nbsp;' }}</td>
        <td>{{ item.stature || '&nbsp;' }}</td>
        <td>{{ item.weight || '&nbsp;' }}</td>
        <td>{{ item.bmi || '&nbsp;' }}</td>
      </tr>
      </tbody>
    </table>

    <Scatter
      v-if="loaded"
      class="measure-graph__line"
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="'line-' + uid"
      dataset-id-key="label"
      :width="width"
      :height="height"
      :style="{minHeight: height + 'px'}"
      ref="graph"
    ></Scatter>
    <v-progress-circular
      v-else
      class="loader-circular"
      indeterminate
      color="primary"
      size="64"
    ></v-progress-circular>
  </div>
</template>

<script>

import measureDB from '@/app/measure'
import ChartJS from '@/app/chartjs'
import { Scatter } from 'vue-chartjs/legacy'
import config from '@/app/config'
import bounds from '@/app/bounds'

const defaultLineOptions = config.graphs.common.defaultLineOptions

ChartJS.init()

export default {
  components: {
    Scatter
  },
  props: {
    type: {
      type: String,
      validator: function (value) {
        // Значение должно соответствовать одной из этих строк
        return [
          measureDB.WEIGHT,
          measureDB.STATURE,
          measureDB.BMI
        ].includes(value)
      },
      default: measureDB.WEIGHT
    },
    measures: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      uid: this._uid,
      width: 0,
      height: 0,
      beforeWidth: 0,
      beforeHeight: 0,
      loaded: false
    }
  },
  computed: {
    isDebug () {
      return typeof this.$route.query.p !== 'undefined'
    },
    measureGraphClass () {
      return {
        ['measure-graph--' + this.type]: true,
        ['measure-graph--sex-' + this.activeProfile.sex]: true,
        'measure-graph--debug': this.isDebug
      }
    },
    lastItems () {
      let items = []
      let cnt = this.measures.length - 1
      for (let i = 0; i < 4; i++) {
        if (typeof (this.measures[cnt - i]) !== 'undefined') {
          items[i] = this.measures[cnt - i]
          items[i].bmi = measureDB.calcBMI(items[i].weight, items[i].stature)
          items[i].age = measureDB.getAgeInHuman(this.activeProfile, items[i])
        } else {
          items[i] = {
            dt: '',
            age: '',
            stature: '',
            weight: '',
            bmi: ''
          }
        }
      }
      return items
    },
    activeProfile () {
      return this.$store.state.profile
    },
    points () {
      let points = []
      for (let i in this.measures) {
        const item = this.measures[i]
        let y
        if (this.type === measureDB.BMI) {
          if (item[measureDB.WEIGHT] && item[measureDB.STATURE]) {
            y = measureDB.calcBMI(item[measureDB.WEIGHT], item[measureDB.STATURE])
          }
        } else if (item[this.type]) {
          y = item[this.type]
        }
        const age = measureDB.getAgeInYears(this.activeProfile, item)
        const value = Math.round(y * 100) / 100
        const point = {
          x: age,
          y: value,
          measure: item,
          // eslint-disable-next-line vue/no-async-in-computed-properties
          bounds: bounds.getPersent(this.type, age, value)
        }
        if (point.x >= 2) {
          points.push(point)
        }
      }
      return points
    },
    persentiles () {
      let persentiles = bounds.getPersintiles(this.type)
      let ret = []
      if (persentiles) {
        for (let lineName of Object.keys(persentiles).reverse()) {
          let color = this.$vuetify.theme.currentTheme.primary
          if (lineName === 'red') {
            color = 'rgba(255, 0, 0, 0.2)'
          }
          ret.push({
            ...defaultLineOptions,
            borderWidth: () => {
              if (['5', '50', '95', 'red'].indexOf(lineName) >= 0) {
                return 3
              }
              return 1
            },
            datalabels: {
              display: false
            },
            fill: lineName === 'red',
            pointRadius: this.isDebug ? 1 : 0,
            pointHoverRadius: this.isDebug ? 1 : 0,
            backgroundColor: color,
            lineBorderColor: color,
            pointBorderColor: color,
            data: persentiles[lineName]
          })

          let point = persentiles[lineName][persentiles[lineName].length - 1]
          ret.push({
            label: lineName,
            datalabels: {
              align: 'center',
              anchor: 'center',
              display: lineName !== 'red'
            },
            data: [
              {
                x: point[0],
                y: point[1],
                tooltip: lineName + '%'
              }
            ]
          })
        }
      }
      return ret
    },
    chartData () {
      return {
        datasets: [
          {
            ...defaultLineOptions,
            label: measureDB.getLabel(this.type),
            backgroundColor: 'white',
            lineBorderColor: config.graphs[this.type].lineColor,
            pointBorderColor: config.graphs[this.type].color,
            data: this.points
          },
          ...this.persentiles
        ]
      }
    },
    chartOptions () {
      const gridX = Object.assign({}, this.isDebug ? config.graphs.common.debugGridOptions : config.graphs.common.defaultGridOptions)
      const gridY = Object.assign({}, this.isDebug ? config.graphs.common.debugGridOptions : config.graphs.common.defaultGridOptions)

      if (!this.isDebug) {
        gridX.color = ({ tick }) => {
          if (parseInt(tick.value) === tick.value) {
            return config.graphs.common.gridMainColor
          }
          return config.graphs.common.gridColor
        }

        gridX.z = -1
        gridY.z = -2

        gridY.color = ({ tick }) => {
          let isActive = tick.value % 5 === 0
          if (this.type === measureDB.BMI) {
            isActive = parseInt(tick.value) === tick.value
          }
          if (isActive) {
            return config.graphs.common.gridMainColor
          }
          return config.graphs.common.gridColor
        }
      }

      const y = {
        min: config.graphs[this.type].yMin,
        max: config.graphs[this.type].yMax,
        position: 'left',
        grid: gridY,
        title: {
          display: !this.isDebug,
          text: this.graphAxeLabel
        },
        ticks: {
          ...config.graphs.common.defaultTicksOptions,
          display: true,
          stepSize: 1,
          maxTicksLimit: 200,
          callback: (value) => {
            return value % 5 === 0 ? value : ''
          }
        }
      }

      if (this.type === measureDB.BMI) {
        y.ticks.stepSize = 0.2
        y.ticks.callback = (value) => {
          return parseInt(value) === value ? value : ''
        }
      }

      const yr = Object.assign({}, y)
      yr.position = 'right'
      yr.title = {
        display: false
      }
      yr.grid = {
        display: false
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        onResize: (chart, size) => {
          // console.log('onResize', { chart, size })
        },
        scales: {
          x: {
            min: config.graphs.common.xMin,
            max: config.graphs.common.xMax,
            grid: gridX,
            title: {
              display: !this.isDebug,
              text: 'Возраст'
            },
            ticks: {
              ...config.graphs.common.defaultTicksOptions,
              display: true,
              stepSize: 0.5,
              callback: (value) => {
                if (parseInt(value) === value) {
                  return value
                }
                return ''
              }
            }
          },
          y,
          yr
        },
        showAllTooltips: true,
        plugins: {
          datalabels: {
            backgroundColor: 'white',
            color: this.$vuetify.theme.currentTheme.primary,
            font: {
              weight: 'bold'
            },
            display: false,
            padding: 1,
            offset: 2,
            align: 'center',
            formatter: function (value, context) {
              return context.dataset.label
            }
          },
          filler: {
            propagate: true
          },
          tooltip: {
            displayColors: false,
            filter: function (tooltipItem) {
              let tooltipIsCoords = tooltipItem.raw && (Array.isArray(tooltipItem.raw) ||
                (typeof tooltipItem.raw.bounds === 'undefined') ||
                isNaN(tooltipItem.raw.bounds.persent))
              return this.isDebug || !tooltipIsCoords
            },
            callbacks: {
              title: function (tooltipItem) {
                return ''
              },
              label: function (tooltipItem) {
                let label = ''
                let coordText = ''
                if (tooltipItem.raw) {
                  if (tooltipItem.raw.tooltip) {
                    label = tooltipItem.raw.tooltip
                  } else if (Array.isArray(tooltipItem.raw)) {
                    coordText = '(' + tooltipItem.raw[0] + ', ' + tooltipItem.raw[1] + ')'
                  } else if (typeof tooltipItem.raw.bounds === 'undefined' || isNaN(tooltipItem.raw.bounds.persent)) {
                    coordText = '(' + tooltipItem.raw.x + ', ' + tooltipItem.raw.y + ')'
                  } else {
                    label = parseInt(tooltipItem.raw.bounds.persent) + '%'
                  }
                }
                if (coordText) {
                  label = coordText
                }
                return label
              }
            }
          }
        }
      }
    },
    graphSrc () {
      const profile = this.$store.state.profile
      return '/g/' + [profile.sex, profile.disability, this.type].join('-') + '.jpg'
    },
    graphTitle () {
      return measureDB.getLabel(this.type)
    },
    graphAxeLabel () {
      return measureDB.getLabel(this.type, true)
    }
  },
  mounted () {
    bounds.init(this.activeProfile.sex, this.activeProfile.disability).then(() => {
      this.loaded = true
    })
    this.onResize()
    // this.beforePrint()
    window.addEventListener('beforeprint', this.beforePrint)
    window.addEventListener('afterprint', this.afterPrint)
  },
  methods: {
    onResize () {
      const rect = this.$refs.graphDiv.getBoundingClientRect()
      this.width = rect.width
      this.height = Math.max(rect.width * 1.34, window.innerHeight - 65 * 2)
    },
    beforePrint () {
      for (let id in ChartJS.instances) {
        let instance = ChartJS.instances[id]
        this.beforeWidth = parseInt(instance.width)
        this.beforeHeight = parseInt(instance.height)
        instance.resize(960, 1300)
        setTimeout(() => {
          let b64 = instance.toBase64Image()
          let i = new Image()
          // Instead of the below, you could save something on the
          // chart that decides what to change this to. This worked for me
          // however:
          i.className = 'printed-image'
          i.src = b64
          let parent = instance.canvas.parentNode
          instance.tempImage = i
          instance.oldDisplay = instance.canvas.style.display
          instance.canvas.style.display = 'none'
          parent.appendChild(i)
        }, 1000)
      }
    },
    afterPrint () {
      for (let id in ChartJS.instances) {
        let instance = ChartJS.instances[id]
        instance.resize(this.beforeWidth, this.beforeHeight)
        if (instance.tempImage) {
          let parent = instance.canvas.parentNode
          parent.removeChild(instance.tempImage)
          instance.canvas.style.display = instance.oldDisplay
          delete instance.oldDisplay
          delete instance.tempImage
        }
      }
    }
  }
}
</script>

<style type="text/stylus" lang="stylus">
  .measure-graph
    position relative
    overflow hidden
    min-height 75vh

    .loader-circular
      height 64px
      width 64px
      position absolute
      top 50%
      left 50%
      margin-left -32px
      margin-top -32px

    &__bg
      position relative
      z-index 1
      width 100%
      opacity 0.8
      visibility hidden

      .measure-graph--debug &
        visibility visible

    &__table
      position absolute
      top 10px
      left 71px
      width 50%
      z-index 3
      background white
      border-collapse collapse

      .measure-graph--bmi &
        left 64px

      .measure-graph--debug &
        top 0.8%
        left 5.6%

      td, th
        border 1px solid grey
        text-align center
        @media screen and (max-width: 800px)
          &
            font-size 2vw

      .measure-graph--debug &
        left 6.3%

      .measure-graph--stature.measure-graph--debug &
        left 6.2%

      .measure-graph--bmi.measure-graph--debug &
        left: 11.2%

    &__line
      position relative
      z-index 2
      width 100%
      padding 0 15px

      .measure-graph--debug &
        position absolute
        top 0.2%
        bottom 1.4%
        left 7.5%
        right 7.5%
        padding 0

        &:after
          content 'Возраст'
          color black
          position absolute
          bottom 15px
          left 50%
          margin-left -40px
          background rgba(255, 255, 255, 0.5)
          padding 4px
          text-transform uppercase
          font-size 15px
          font-weight bold

        &.measure-graph--sex-male
          color #2377CD

        &.measure-graph--sex-female
          color #D82686
      .measure-graph--bmi.measure-graph--debug &
        left 8.5%
        right 8.5%
</style>
